import { useEffect, useState } from 'react';

import {
  getVenuaPlannerSerieTransportFlightPlan,
  getVenuaPlannerTermTransportFlightPlan,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type {
  FlightPlanCellResponse,
  TransportSchemeTicketResponse,
} from '@/api/generated/schemas';
import type { TransportSchemeScope } from './model/types';

interface Options {
  productId: string;
  serieId: string;
  termId?: string;
  scope: TransportSchemeScope;
  tickets: TransportSchemeTicketResponse[];
}

export interface FlightPlanState {
  cells: FlightPlanCellResponse[];
  /** Pokrytí legu klíčované `ticketId:facilityId`. */
  coverage: Map<string, { covered: number; total: number }>;
  isLoading: boolean;
}

/**
 * Skutečné časy letů pro matici. Je to **druhý, pomalejší dotaz** — panely směrů stojí kompletní už
 * po `chain`, tady se jen doplní buňky a pokrytí, takže se na čtení legacy spojů nečeká zbytečně.
 *
 * Dotaz jede nad **neuloženým** řetězem, proto ne přes React Query klíč odvozený z URL, ale ručně při
 * každé změně seznamu ticketů.
 */
export function useFlightPlan({
  productId,
  serieId,
  termId,
  scope,
  tickets,
}: Options): FlightPlanState {
  const [state, setState] = useState<FlightPlanState>({
    cells: [],
    coverage: new Map(),
    isLoading: false,
  });

  // Spojené lety mají v matici vlastní sloupce, takže se spoje hledají i pro ně. Autobusová strana
  // dopravu nemá — tam není co načítat.
  const primary = tickets.filter((ticket) => ticket.regularTransportId != null);
  const signature = primary
    .map(
      (t) =>
        `${t.ticketId}:${t.regularTransportId}:${t.kind}:${t.outboundSequence}:${t.inboundSequence}`
    )
    .join('|');

  useEffect(() => {
    if (primary.length === 0) {
      setState({ cells: [], coverage: new Map(), isLoading: false });
      return;
    }

    let cancelled = false;
    setState((current) => ({ ...current, isLoading: true }));

    const body = {
      tickets: primary.map((ticket) => ({
        ticketId: ticket.ticketId,
        regularTransportId: ticket.regularTransportId as number,
        outboundSequence: ticket.outboundSequence,
        inboundSequence: ticket.inboundSequence,
        kind: ticket.kind,
      })),
    };

    const request =
      scope === 'serie'
        ? getVenuaPlannerSerieTransportFlightPlan(productId, serieId, body)
        : getVenuaPlannerTermTransportFlightPlan(productId, serieId, termId ?? '', body);

    void request
      .then((response) => {
        if (cancelled) return;
        const coverage = new Map<string, { covered: number; total: number }>();
        response.coverage.forEach((entry) => {
          coverage.set(`${entry.ticketId}:${entry.facilityId}`, {
            covered: entry.covered,
            total: entry.total,
          });
        });
        setState({ cells: response.cells, coverage, isLoading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ cells: [], coverage: new Map(), isLoading: false });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, serieId, termId, scope, signature]);

  return state;
}
